import { validateTemplateTokens } from "./templateVariables";

/**
 * Shared helpers for the admin Automations tab: state / run metadata, schedule
 * + relative-time formatting, delay conversions, the editor form ⇄ API payload
 * mapping and the client-side validation that mirrors the backend rules in
 * website/automations/validation.py (the backend stays the source of truth —
 * its 400 `error` is always surfaced inline).
 */

// ---------------------------------------------------------------------------
// States / run statuses
// ---------------------------------------------------------------------------

export const AUTOMATION_STATES = ["draft", "active", "paused"];

/** `tone` maps 1:1 onto the design-system StatusBadge variants. */
export const AUTOMATION_STATE_META = {
  draft: {
    label: "Draft",
    tone: "neutral",
    description: "Never sends. Preview and dry runs only — activate it when the preview looks right.",
  },
  active: {
    label: "Active",
    tone: "success",
    description: "Live — routine automations run on their schedule; event automations fire when their event happens.",
  },
  paused: {
    label: "Paused",
    tone: "warning",
    description: "Skipped by the scheduler and by events. Memberships are kept, so resuming does not re-send to users already inside.",
  },
};

export const getStateMeta = (state) =>
  AUTOMATION_STATE_META[state] || { label: state || "Unknown", tone: "neutral", description: "" };

export const RUN_STATUS_META = {
  running: { label: "Running", tone: "info" },
  completed: { label: "Completed", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
};

export const getRunStatusMeta = (status) =>
  RUN_STATUS_META[status] || { label: status || "Unknown", tone: "neutral" };

// ---------------------------------------------------------------------------
// Schedule (routine trigger: days of week, Monday=0 … Sunday=6, + hour UTC)
// ---------------------------------------------------------------------------

export const WEEKDAYS = [
  { value: 0, short: "Mon", long: "Monday" },
  { value: 1, short: "Tue", long: "Tuesday" },
  { value: 2, short: "Wed", long: "Wednesday" },
  { value: 3, short: "Thu", long: "Thursday" },
  { value: 4, short: "Fri", long: "Friday" },
  { value: 5, short: "Sat", long: "Saturday" },
  { value: 6, short: "Sun", long: "Sunday" },
];

export const ALL_DAYS = WEEKDAYS.map((d) => d.value);
export const WEEKDAY_DAYS = [0, 1, 2, 3, 4];
export const WEEKEND_DAYS = [5, 6];

const pad2 = (n) => String(n).padStart(2, "0");

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${pad2(hour)}:00`,
}));

export const formatHourUtc = (hour) => {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return "—";
  return `${pad2(h)}:00 UTC`;
};

const normalizeDays = (days) =>
  Array.from(new Set((Array.isArray(days) ? days : []).map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))).sort(
    (a, b) => a - b
  );

/** "Every day" | "Mon–Fri" | "Sat, Sun" | "Mon, Wed, Fri" | "No days". */
export const formatRunDays = (days) => {
  const list = normalizeDays(days);
  if (list.length === 0) return "No days";
  if (list.length === 7) return "Every day";
  const isConsecutive = list.every((d, i) => i === 0 || d === list[i - 1] + 1);
  if (isConsecutive && list.length >= 3) {
    return `${WEEKDAYS[list[0]].short}–${WEEKDAYS[list[list.length - 1]].short}`;
  }
  return list.map((d) => WEEKDAYS[d].short).join(", ");
};

export const formatSchedule = (automation) =>
  `${formatRunDays(automation?.run_days_of_week)} · ${formatHourUtc(automation?.run_hour_utc)}`;

// ---------------------------------------------------------------------------
// Trigger types (routine schedule | event)
// ---------------------------------------------------------------------------

export const TRIGGER_TYPES = ["routine", "event"];

export const TRIGGER_TYPE_META = {
  routine: {
    label: "Routine schedule",
    short: "Routine",
    description: "Scans everyone on a schedule (days + hour UTC) and fires once per user, when they enter the situation.",
  },
  event: {
    label: "Event",
    short: "Event",
    description:
      "Fires immediately, every time the event happens to a user. No situation — the event already names the user; guards and the delivery policy still apply.",
  },
};

export const getTriggerMeta = (type) =>
  TRIGGER_TYPE_META[type] || { label: type || "Unknown", short: type || "—", description: "" };

/** Label of an event key from the conditions meta (falls back to the key). */
export const findEventLabel = (events, key) => {
  if (!key) return "";
  const match = (events || []).find((e) => e.key === key);
  return match?.label || key;
};

/** "Mon–Fri · 09:00 UTC" for routine automations, "On: Website created" for event automations. */
export const formatTrigger = (automation, meta) => {
  if (automation?.trigger_type === "event") {
    const label = automation.trigger_event_label || findEventLabel(meta?.events, automation.trigger_event);
    return `On: ${label || "—"}`;
  }
  return formatSchedule(automation);
};

/** JS getUTCDay() (Sun=0) → our Monday=0 convention. */
const utcWeekdayMon0 = (date) => (date.getUTCDay() + 6) % 7;

/**
 * Next UTC instant at `hour`:00 on one of `days` strictly after `now`
 * (null when no days are selected or the hour is invalid).
 */
export const computeNextRun = (days, hour, now = new Date()) => {
  const list = normalizeDays(days);
  const h = Number(hour);
  if (list.length === 0 || !Number.isInteger(h) || h < 0 || h > 23) return null;
  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset, h, 0, 0, 0)
    );
    if (candidate.getTime() <= now.getTime()) continue;
    if (list.includes(utcWeekdayMon0(candidate))) return candidate;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

export const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return String(isoString);
  return d.toLocaleString();
};

export const formatUtcDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  const weekday = WEEKDAYS[utcWeekdayMon0(date)].short;
  return `${weekday} ${date.getUTCDate()} ${date.toLocaleString("en-US", { month: "short", timeZone: "UTC" })}, ${pad2(
    date.getUTCHours()
  )}:${pad2(date.getUTCMinutes())} UTC`;
};

const RELATIVE_UNITS = [
  { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: "week", ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: "day", ms: 24 * 60 * 60 * 1000 },
  { unit: "hour", ms: 60 * 60 * 1000 },
  { unit: "minute", ms: 60 * 1000 },
];

/** "just now" | "5 minutes ago" | "in 3 hours" … (Intl when available). */
export const formatRelativeTime = (isoOrDate, now = new Date()) => {
  if (!isoOrDate) return "—";
  const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(date.getTime())) return String(isoOrDate);
  const diff = date.getTime() - now.getTime();
  const abs = Math.abs(diff);
  if (abs < 45 * 1000) return "just now";
  const match = RELATIVE_UNITS.find((u) => abs >= u.ms) || RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
  const value = Math.round(diff / match.ms);
  try {
    if (typeof Intl !== "undefined" && typeof Intl.RelativeTimeFormat === "function") {
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(value, match.unit);
    }
  } catch {
    // fall through to the manual format
  }
  const n = Math.abs(value);
  const label = `${n} ${match.unit}${n === 1 ? "" : "s"}`;
  return value < 0 ? `${label} ago` : `in ${label}`;
};

// ---------------------------------------------------------------------------
// Action delays (stored as minutes; edited as days + hours)
// ---------------------------------------------------------------------------

export const minutesToDaysHours = (minutes) => {
  const total = Math.max(0, Math.trunc(Number(minutes) || 0));
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total - days * 24 * 60) / 60);
  return { days, hours };
};

export const daysHoursToMinutes = (days, hours) => {
  const d = Math.max(0, Math.trunc(Number(days) || 0));
  const h = Math.max(0, Math.trunc(Number(hours) || 0));
  return d * 24 * 60 + h * 60;
};

export const formatDelay = (minutes) => {
  const total = Math.max(0, Math.trunc(Number(minutes) || 0));
  if (total === 0) return "No delay";
  const { days, hours } = minutesToDaysHours(total);
  const rest = total - days * 24 * 60 - hours * 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (rest) parts.push(`${rest}m`);
  return `+${parts.join(" ")}`;
};

// ---------------------------------------------------------------------------
// Channels / email variants / policy reasons
// ---------------------------------------------------------------------------

export const CHANNEL_META = {
  inbox: { label: "Inbox", description: "In-app inbox message." },
  email: { label: "Email", description: "Email, rendered with the chosen template variant." },
};

export const EMAIL_VARIANT_META = {
  default: {
    label: "Default",
    hint: "Standard transactional layout. Sent regardless of the recipient's marketing preference.",
  },
  marketing: {
    label: "Marketing",
    hint: "Marketing layout with the unsubscribe footer. Only delivered to users who opted in to marketing emails — everyone else is suppressed at send time.",
  },
  product_update: {
    label: "Product update",
    hint: "Product-update layout for announcements about new features and changes.",
  },
};

export const formatEmailVariant = (variant) =>
  EMAIL_VARIANT_META[variant]?.label ||
  String(variant || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

/** Which channels any action in the automation uses. */
export const summarizeChannels = (actions) => {
  const list = Array.isArray(actions) ? actions : [];
  return {
    inbox: list.some((a) => Boolean(a?.channels?.inbox)),
    email: list.some((a) => Boolean(a?.channels?.email)),
  };
};

export const uniqueEmailVariants = (actions) =>
  Array.from(
    new Set(
      (Array.isArray(actions) ? actions : [])
        .filter((a) => Boolean(a?.channels?.email))
        .map((a) => a?.email_template_variant || "default")
    )
  );

const POLICY_REASON_LABELS = {
  suppressed: "Email address suppressed (bounce / complaint)",
  marketing_opt_out: "Opted out of marketing emails",
};

/** Human label for a preview/run policy reason (e.g. "frequency_cap_7d"). */
export const formatPolicyReason = (reason) => {
  if (!reason) return "Unknown reason";
  if (POLICY_REASON_LABELS[reason]) return POLICY_REASON_LABELS[reason];
  const cap = /^frequency_cap_(\d+)d$/.exec(reason);
  if (cap) return `${cap[1]}-day frequency cap reached`;
  return String(reason).replace(/_/g, " ");
};

// ---------------------------------------------------------------------------
// Conditions metadata helpers
// ---------------------------------------------------------------------------

export const findCondition = (conditions, key) =>
  (Array.isArray(conditions) ? conditions : []).find((c) => c?.key === key) || null;

const formatParamValue = (spec, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (spec?.type === "bool") return value ? "yes" : "no";
  return String(value);
};

/** "Days until expiry: 7 · Automation: welcome" (falls back to raw key=value). */
export const formatParamsSummary = (condition, params) => {
  const values = params && typeof params === "object" ? params : {};
  const schema = Array.isArray(condition?.params_schema) ? condition.params_schema : [];
  if (schema.length > 0) {
    return schema.map((spec) => `${spec.label || spec.name}: ${formatParamValue(spec, values[spec.name])}`).join(" · ");
  }
  const entries = Object.entries(values);
  if (entries.length === 0) return "";
  return entries.map(([k, v]) => `${k}=${formatParamValue(null, v)}`).join(" · ");
};

/** Default params for a condition (from its schema defaults). */
export const defaultParamsFor = (condition) => {
  const out = {};
  (condition?.params_schema || []).forEach((spec) => {
    if (spec.default !== null && spec.default !== undefined) out[spec.name] = spec.default;
    else if (spec.type === "bool") out[spec.name] = false;
    else out[spec.name] = "";
  });
  return out;
};

// ---------------------------------------------------------------------------
// Editor form model
// ---------------------------------------------------------------------------

let localKeyCounter = 0;
export const makeLocalKey = (prefix = "k") => {
  localKeyCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${localKeyCounter}`;
};

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export const SLUG_PATTERN = /^[-a-zA-Z0-9_]+$/;

export const MAX_ACTION_TITLE_LENGTH = 200;
export const MAX_ACTION_BODY_LENGTH = 20000;
export const DEFAULT_PRIORITY = 100;

export const emptyAction = (defaultSender = "") => ({
  _key: makeLocalKey("action"),
  channels: { inbox: true, email: false },
  email_template_variant: "default",
  sender_display_name: defaultSender || "",
  title: "",
  body_markdown: "",
  delay_days: "0",
  delay_hours: "0",
});

export const emptyGuard = () => ({ _key: makeLocalKey("guard"), key: "", params: {} });

export const emptyAutomationForm = (meta) => ({
  id: null,
  name: "",
  slug: "",
  slugTouched: false,
  description: "",
  state: "draft",
  trigger_type: "routine",
  trigger_event: "",
  run_days_of_week: [...ALL_DAYS],
  run_hour_utc: "9",
  situation_key: "",
  situation_params: {},
  guards: [],
  actions: [emptyAction(meta?.default_sender_display_name || "")],
  priority: String(DEFAULT_PRIORITY),
  ignore_frequency_caps: false,
  respect_quiet_hours: true,
});

const actionToForm = (action, defaultSender) => {
  const { days, hours } = minutesToDaysHours(action?.delay_minutes);
  return {
    _key: makeLocalKey("action"),
    channels: {
      inbox: Boolean(action?.channels?.inbox),
      email: Boolean(action?.channels?.email),
    },
    email_template_variant: action?.email_template_variant || "default",
    sender_display_name: action?.sender_display_name ?? defaultSender ?? "",
    title: action?.title || "",
    body_markdown: action?.body_markdown || "",
    delay_days: String(days),
    delay_hours: String(hours),
  };
};

/**
 * Saved automation → editor form. `duplicate` strips the identity (new draft,
 * "Copy of …" name, "<slug>-copy" slug).
 */
export const automationToForm = (automation, meta, { duplicate = false } = {}) => {
  const defaultSender = meta?.default_sender_display_name || "";
  const base = emptyAutomationForm(meta);
  if (!automation) return base;
  const actions = (automation.actions || []).map((a) => actionToForm(a, defaultSender));
  return {
    ...base,
    id: duplicate ? null : automation.id ?? null,
    name: duplicate ? `Copy of ${automation.name || ""}`.trim() : automation.name || "",
    slug: duplicate ? slugify(`${automation.slug || ""}-copy`) : automation.slug || "",
    slugTouched: true,
    description: automation.description || "",
    state: duplicate ? "draft" : automation.state || "draft",
    trigger_type: automation.trigger_type || "routine",
    trigger_event: automation.trigger_event || "",
    run_days_of_week: normalizeDays(automation.run_days_of_week),
    run_hour_utc: String(automation.run_hour_utc ?? 9),
    situation_key: automation.situation_key || "",
    situation_params: { ...(automation.situation_params || {}) },
    guards: (automation.guards || []).map((g) => ({
      _key: makeLocalKey("guard"),
      key: g?.key || "",
      params: { ...(g?.params || {}) },
    })),
    actions: actions.length > 0 ? actions : base.actions,
    priority: String(automation.priority ?? DEFAULT_PRIORITY),
    ignore_frequency_caps: Boolean(automation.ignore_frequency_caps),
    respect_quiet_hours: automation.respect_quiet_hours !== false,
  };
};

const INTEGER_TEXT = /^-?\d+$/;

/** Drop blank params; send whole-number text as numbers (matches what the API returns). */
const cleanParams = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    out[k] = typeof v === "string" && INTEGER_TEXT.test(v.trim()) ? Number(v.trim()) : v;
  });
  return out;
};

const parseIntOr = (value, fallback) => {
  const s = String(value ?? "").trim();
  if (!s) return fallback;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

/** Editor form → API payload (create / PATCH / preview). */
export const formToPayload = (form) => ({
  name: String(form.name || "").trim(),
  slug: String(form.slug || "").trim(),
  description: String(form.description || "").trim(),
  state: form.state || "draft",
  trigger_type: form.trigger_type || "routine",
  trigger_event: form.trigger_type === "event" ? String(form.trigger_event || "").trim() : "",
  run_days_of_week: normalizeDays(form.run_days_of_week),
  run_hour_utc: parseIntOr(form.run_hour_utc, 9),
  situation_key: form.situation_key || "",
  situation_params: cleanParams(form.situation_params),
  guards: (form.guards || []).map((g) => ({ key: g.key || "", params: cleanParams(g.params) })),
  actions: (form.actions || []).map((a) => ({
    channels: { inbox: Boolean(a.channels?.inbox), email: Boolean(a.channels?.email) },
    email_template_variant: a.email_template_variant || "default",
    sender_display_name: String(a.sender_display_name || "").trim(),
    title: String(a.title || "").trim(),
    body_markdown: String(a.body_markdown || ""),
    delay_minutes: daysHoursToMinutes(a.delay_days, a.delay_hours),
  })),
  priority: parseIntOr(form.priority, DEFAULT_PRIORITY),
  ignore_frequency_caps: Boolean(form.ignore_frequency_caps),
  respect_quiet_hours: Boolean(form.respect_quiet_hours),
});

/** Saved automation summary → preview payload (includes `id` so memberships count). */
export const automationToPreviewPayload = (automation) => ({
  id: automation.id,
  name: automation.name,
  slug: automation.slug,
  description: automation.description || "",
  state: automation.state,
  trigger_type: automation.trigger_type,
  trigger_event: automation.trigger_event || "",
  run_days_of_week: automation.run_days_of_week,
  run_hour_utc: automation.run_hour_utc,
  situation_key: automation.situation_key,
  situation_params: automation.situation_params || {},
  guards: automation.guards || [],
  actions: automation.actions || [],
  priority: automation.priority,
  ignore_frequency_caps: Boolean(automation.ignore_frequency_caps),
  respect_quiet_hours: Boolean(automation.respect_quiet_hours),
});

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

const validateParams = (condition, params, label, fieldPrefix, errors) => {
  (condition?.params_schema || []).forEach((spec) => {
    const field = `${fieldPrefix}.${spec.name}`;
    const name = spec.label || spec.name;
    const value = params?.[spec.name];
    const missing = isBlank(value) && spec.type !== "bool";
    if (missing) {
      if (spec.required && isBlank(spec.default)) {
        errors.push({ field, message: `${label}: "${name}" is required.` });
      }
      return;
    }
    if (spec.type === "int") {
      const n = Number(value);
      if (!Number.isInteger(n)) {
        errors.push({ field, message: `${label}: "${name}" must be a whole number.` });
      } else {
        if (spec.min !== null && spec.min !== undefined && n < spec.min) {
          errors.push({ field, message: `${label}: "${name}" must be at least ${spec.min}.` });
        }
        if (spec.max !== null && spec.max !== undefined && n > spec.max) {
          errors.push({ field, message: `${label}: "${name}" must be at most ${spec.max}.` });
        }
      }
    }
    if (spec.type === "automation_slug" && !SLUG_PATTERN.test(String(value).trim())) {
      errors.push({ field, message: `${label}: "${name}" must be a slug.` });
    }
  });
};

/**
 * Client-side mirror of the backend rules — used to disable Save and to tell
 * the admin what is missing before the live preview can run. Returns
 * [{ field, message }] (empty when valid).
 */
export const validateAutomationForm = (form, meta) => {
  const errors = [];
  if (isBlank(form.name)) errors.push({ field: "name", message: "Name is required." });
  if (isBlank(form.slug)) errors.push({ field: "slug", message: "Slug is required." });
  else if (!SLUG_PATTERN.test(String(form.slug).trim())) {
    errors.push({ field: "slug", message: "Slug may only contain letters, digits, hyphens and underscores." });
  }
  const isEvent = form.trigger_type === "event";
  if (isEvent) {
    // Event automations: the event is the trigger; no schedule, no situation.
    if (isBlank(form.trigger_event)) {
      errors.push({ field: "trigger_event", message: "Choose the event this automation listens for." });
    } else if (Array.isArray(meta?.events) && meta.events.length > 0 && !meta.events.some((e) => e.key === form.trigger_event)) {
      errors.push({ field: "trigger_event", message: "Unknown event — pick one from the list." });
    }
  } else {
    if (normalizeDays(form.run_days_of_week).length === 0) {
      errors.push({ field: "run_days_of_week", message: "Pick at least one run day." });
    }
    const hour = Number(form.run_hour_utc);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      errors.push({ field: "run_hour_utc", message: "Run hour must be between 00:00 and 23:00 UTC." });
    }
    if (isBlank(form.situation_key)) {
      errors.push({ field: "situation_key", message: "Choose a situation." });
    } else {
      const situation = findCondition(meta?.situations, form.situation_key);
      if (situation) validateParams(situation, form.situation_params, "Situation", "situation_params", errors);
    }
  }
  (form.guards || []).forEach((guard, index) => {
    const label = `Guard #${index + 1}`;
    if (isBlank(guard.key)) {
      errors.push({ field: `guards.${index}.key`, message: `${label}: choose a guard or remove the row.` });
      return;
    }
    const condition = findCondition(meta?.guards, guard.key);
    if (condition) validateParams(condition, guard.params, label, `guards.${index}.params`, errors);
  });
  if (!Array.isArray(form.actions) || form.actions.length === 0) {
    errors.push({ field: "actions", message: "Add at least one action." });
  }
  (form.actions || []).forEach((action, index) => {
    const label = `Action #${index + 1}`;
    if (!action.channels?.inbox && !action.channels?.email) {
      errors.push({ field: `actions.${index}.channels`, message: `${label}: enable at least one channel (inbox or email).` });
    }
    if (isBlank(action.title)) errors.push({ field: `actions.${index}.title`, message: `${label}: title is required.` });
    else if (String(action.title).trim().length > MAX_ACTION_TITLE_LENGTH) {
      errors.push({ field: `actions.${index}.title`, message: `${label}: title must be at most ${MAX_ACTION_TITLE_LENGTH} characters.` });
    }
    if (isBlank(action.body_markdown)) errors.push({ field: `actions.${index}.body_markdown`, message: `${label}: body is required.` });
    else if (String(action.body_markdown).length > MAX_ACTION_BODY_LENGTH) {
      errors.push({ field: `actions.${index}.body_markdown`, message: `${label}: body must be at most ${MAX_ACTION_BODY_LENGTH} characters.` });
    }
    // Message variables must exist and use an allowed format (mirrors the backend's validate_template).
    validateTemplateTokens(action.title, meta?.variables).forEach((p) =>
      errors.push({ field: `actions.${index}.title`, message: `${label} title: ${p.message}` })
    );
    validateTemplateTokens(action.body_markdown, meta?.variables).forEach((p) =>
      errors.push({ field: `actions.${index}.body_markdown`, message: `${label} body: ${p.message}` })
    );
    const days = Number(action.delay_days);
    const hours = Number(action.delay_hours);
    if (!Number.isInteger(days) || days < 0) errors.push({ field: `actions.${index}.delay_days`, message: `${label}: delay days must be 0 or more.` });
    if (!Number.isInteger(hours) || hours < 0 || hours > 23) {
      errors.push({ field: `actions.${index}.delay_hours`, message: `${label}: delay hours must be between 0 and 23.` });
    }
  });
  if (!isBlank(form.priority) && !Number.isInteger(Number(form.priority))) {
    errors.push({ field: "priority", message: "Priority must be a whole number." });
  }
  return errors;
};

/** Map of field → first message, handy for inline `error` props. */
export const errorsByField = (errors) => {
  const out = {};
  (errors || []).forEach((e) => {
    if (!out[e.field]) out[e.field] = e.message;
  });
  return out;
};

// ---------------------------------------------------------------------------
// Aggregates for the page summary strip
// ---------------------------------------------------------------------------

export const summarizeAutomations = (automations) => {
  const list = Array.isArray(automations) ? automations : [];
  const summary = { total: list.length, draft: 0, active: 0, paused: 0, scheduled: 0, sent: 0, failed: 0, suppressed: 0 };
  list.forEach((a) => {
    if (summary[a.state] !== undefined) summary[a.state] += 1;
    const counts = a.message_counts || {};
    summary.scheduled += Number(counts.scheduled || 0);
    summary.sent += Number(counts.sent || 0);
    summary.failed += Number(counts.failed || 0);
    summary.suppressed += Number(counts.suppressed || 0);
  });
  return summary;
};

export const formatCount = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  try {
    return value.toLocaleString();
  } catch {
    return String(value);
  }
};
